// Modal de confirmação pra excluir viagem.
//
// Comportamento dual baseado em ROLE do user na viagem:
//   - ADMIN (auth.uid === trip.userId): apaga a viagem e tudo dentro
//     (deleteTripWithChildren). Operação irreversível.
//   - CONVIDADO (auth.uid in trip.memberIds, mas NÃO é admin): só remove
//     o user de `memberIds` e `members` — perde acesso, viagem continua
//     existindo pra os outros.
//
// Texto do modal muda conforme o role pra ficar claro o que vai acontecer.

import { auth, db } from '../../core/firebase.js';
import {
  doc, updateDoc, arrayRemove, deleteField,
} from 'firebase/firestore';
import { deleteTripWithChildren } from '../../core/firestore.js';
import { toast } from '../../shared/components/toast.js';

export function openDeleteTripModal(trip) {
  return new Promise((resolve) => {
    const uid = auth.currentUser?.uid;
    const isAdmin = uid && uid === trip.userId;
    const isMember = uid && (trip.memberIds || []).includes(uid);

    const overlay = document.createElement('div');
    overlay.className = 'settings-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = MODAL_HTML(trip, { isAdmin, isMember });
    document.body.appendChild(overlay);

    function close(result) {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(result);
    }
    function onKey(e) { if (e.key === 'Escape') close(null); }
    document.addEventListener('keydown', onKey);

    overlay.addEventListener('click', async (e) => {
      if (e.target === overlay) close(null);
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (!action) return;
      e.preventDefault();

      if (action === 'cancel') return close(null);

      if (action === 'confirm') {
        const btn = e.target.closest('[data-action="confirm"]');
        btn.disabled = true;
        btn.textContent = isAdmin ? 'Excluindo…' : 'Saindo…';
        try {
          if (isAdmin) {
            await deleteTripWithChildren(trip.id);
            toast.success('Viagem removida');
            close({ kind: 'deleted' });
          } else if (isMember) {
            // Sai do roteiro: remove uid de memberIds e tira a entrada em members
            await updateDoc(doc(db, 'trips', trip.id), {
              memberIds: arrayRemove(uid),
              [`members.${uid}`]: deleteField(),
            });
            toast.success('Você saiu da viagem');
            close({ kind: 'left' });
          } else {
            close(null);
          }
        } catch (err) {
          console.error('[deleteTrip] failed:', err?.code, err?.message, err);
          btn.disabled = false;
          btn.textContent = isAdmin ? 'Excluir viagem' : 'Sair da viagem';
          toast.error(
            isAdmin
              ? 'Não consegui excluir a viagem. Tente novamente.'
              : 'Não consegui sair da viagem. Tente novamente.',
          );
        }
      }
    });
  });
}

function MODAL_HTML(trip, { isAdmin, isMember }) {
  if (isAdmin) {
    return `
      <div class="settings-modal-card glass-sheet">
        <div class="settings-modal-head">
          <p class="eyebrow settings-modal-eyebrow" style="color:var(--danger)">Atenção · Ação irreversível</p>
          <h3 class="h3 settings-modal-title">Excluir "${esc(trip.name || 'viagem')}"?</h3>
          <p class="settings-modal-help">
            A viagem inteira (roteiro, hotéis, rotas, transações, documentos e
            convites) será apagada permanentemente. Ninguém vai conseguir
            recuperar — nem você, nem os outros membros.
          </p>
        </div>
        <div class="settings-modal-foot">
          <button type="button" class="btn btn-ghost" data-action="cancel">Cancelar</button>
          <button type="button" class="btn btn-destructive" data-action="confirm">Excluir viagem</button>
        </div>
      </div>
    `;
  }

  if (isMember) {
    return `
      <div class="settings-modal-card glass-sheet">
        <div class="settings-modal-head">
          <p class="eyebrow settings-modal-eyebrow">Sair de "${esc(trip.name || 'viagem')}"</p>
          <h3 class="h3 settings-modal-title">Você quer sair dessa viagem?</h3>
          <p class="settings-modal-help">
            Você perde acesso ao roteiro, hotéis, rotas e documentos dessa
            viagem. A viagem continua existindo pra quem criou e os outros
            membros. Pra voltar, peça um link de convite ao admin.
          </p>
        </div>
        <div class="settings-modal-foot">
          <button type="button" class="btn btn-ghost" data-action="cancel">Cancelar</button>
          <button type="button" class="btn btn-destructive" data-action="confirm">Sair da viagem</button>
        </div>
      </div>
    `;
  }

  // Caso muito raro: nem admin nem membro (não deveria estar vendo o card)
  return `
    <div class="settings-modal-card glass-sheet">
      <div class="settings-modal-head">
        <h3 class="h3 settings-modal-title">Sem permissão</h3>
        <p class="settings-modal-help">Você não tem acesso a essa viagem.</p>
      </div>
      <div class="settings-modal-foot">
        <button type="button" class="btn btn-primary" data-action="cancel">Fechar</button>
      </div>
    </div>
  `;
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
